import { RefCallback, memo, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Spinner } from '@vp/swan'
import classNames from 'classnames'

import { useTranslations } from 'client/hooks/useTranslations'
import { RenderProperty } from 'shared/renderProperties'
import { Tile, TileHeader } from 'client/components/common/Tile'
import { useInView } from 'react-intersection-observer'
import { contentUpdate } from '~/client/store/content/actions'
import { stringRenderPropertySelector } from '~/client/store/config/reducer'
import { getIsLoadMoreAvailable } from '~/client/store/config/selectors'
import {
  INITIAL_SCROLL_POSITION,
  LOAD_MORE_DELAY,
  MOBILE_ROOT_MARGIN,
  ROOT_MARGIN,
  TileSizeDesktop,
  TileSizeMobile,
} from 'client/components/Gallery/PreviewArea/DesignSkeleton/data'
import { isSmallScreen } from '~/client/utils/deviceDetection'
import { ThunkDispatch } from '~/client/store/interface'

const DesignSkeletonComponent = () => {
  const localize = useTranslations()
  const dispatch = useDispatch<ThunkDispatch>()

  const stringRenderProperty = useSelector(stringRenderPropertySelector)
  const isLoadMoreAvailable = useSelector(getIsLoadMoreAvailable)

  const tilePreviewSize = stringRenderProperty(RenderProperty.TilePreviewSize)

  const isLargePreviewSize = tilePreviewSize === 'Large'
  const skeletonSizeDesktop = isLargePreviewSize ? TileSizeDesktop.Large : TileSizeDesktop.Default
  const skeletonSizeMobile = isLargePreviewSize ? TileSizeMobile.Large : TileSizeMobile.Default
  const [skeletonSize, rootMargin] = isSmallScreen() ? [skeletonSizeMobile, MOBILE_ROOT_MARGIN] : [skeletonSizeDesktop, ROOT_MARGIN]

  const scrollState = useRef({
    currentY: 0,
    prevY: 0,
    isFirstLoad: true,
  })

  const { ref: firstElementRef, inView: firstElementInView } = useInView({ rootMargin })
  const { ref: lastElementRef, inView: lastElementInView } = useInView({ rootMargin })

  const inView = firstElementInView || lastElementInView
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const skeletonTiles = useMemo(() => [...Array(skeletonSize).keys()], [skeletonSize])

  const shouldLoadMore = inView && isLoadMoreAvailable

  useEffect(() => {
    // Timeout adds delay before loading more. Сonveniently for Ratings & Reviews link
    clearTimeout(timerRef.current ?? 0)

    if (shouldLoadMore) {
      scrollState.current.prevY = scrollState.current.currentY
      scrollState.current.currentY = window.scrollY

      const shouldScrollToCurrentPosition = isSmallScreen() && scrollState.current.isFirstLoad && scrollState.current.prevY > 0

      if (shouldScrollToCurrentPosition) {
        window.scrollTo(0, INITIAL_SCROLL_POSITION)
        scrollState.current.isFirstLoad = false
      }
      timerRef.current = setTimeout(() => {
        dispatch(contentUpdate({ shouldUpdateUrl: false, loadMore: true }))
      }, LOAD_MORE_DELAY)
    }

    return () => clearTimeout(timerRef.current ?? 0)
  }, [dispatch, shouldLoadMore])

  // It returns ref callback for the first or last skeleton tiles
  const getSkeletonTileRef = (index: number): RefCallback<Element> | undefined => {
    if (index === 0) {
      return firstElementRef
    }

    if (isSmallScreen() && index === skeletonSize - 1) {
      return lastElementRef
    }

    return undefined
  }

  return (
    <>
      {skeletonTiles.map((key) => (
        <Tile className="template-tile-skeleton-wave" key={key} ref={getSkeletonTileRef(key)} type="design-tile">
          <TileHeader>
            <div className="tile-preview">
              <div className={classNames('skeleton-height', { 'skeleton-height-large': isLargePreviewSize })}>
                <div className="mcp-img-loading">
                  <Spinner accessibleText={localize('Loading')} size="standard" />
                </div>
              </div>
            </div>
          </TileHeader>
        </Tile>
      ))}
    </>
  )
}

export const DesignSkeleton = memo(DesignSkeletonComponent)
